import { useEffect, useMemo, useRef, useState } from 'react';

import type { WebNodeConfig } from './spiderweb-types';
import type { NodePositionPct } from './use-spiderweb-phase-positions';

const AMBIENT_FRAME_INTERVAL_MS = 1000 / 30;
const AMBIENT_OVERLAP_FRAME_INTERVAL_MS = 1000 / 15;
const AMBIENT_MAX_FRAME_DELTA_MS = 100;
const AMBIENT_MAX_OFFSET_PCT = 0.8;
const AMBIENT_MIN_CYCLE_SEC = 16;
const AMBIENT_CYCLE_RANGE_SEC = 10;
const TAU = Math.PI * 2;

type AmbientMotionProfile = {
	xAmplitude: number;
	yAmplitude: number;
	xPhase: number;
	yPhase: number;
	xCycleSec: number;
	yCycleSec: number;
};

function hashString(value: string): number {
	let hash = 2166136261;
	for (let i = 0; i < value.length; i++) {
		hash ^= value.charCodeAt(i);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
}

function hashFraction(value: string, salt: string): number {
	return hashString(`${salt}:${value}`) / 0xffffffff;
}

export function getAmbientMotionProfile(nodeId: string): AmbientMotionProfile {
	const xAmplitude = AMBIENT_MAX_OFFSET_PCT * (0.55 + hashFraction(nodeId, 'xa') * 0.45);
	const yAmplitude = AMBIENT_MAX_OFFSET_PCT * (0.55 + hashFraction(nodeId, 'ya') * 0.45);

	return {
		xAmplitude,
		yAmplitude,
		xPhase: hashFraction(nodeId, 'xp') * TAU,
		yPhase: hashFraction(nodeId, 'yp') * TAU,
		xCycleSec: AMBIENT_MIN_CYCLE_SEC + hashFraction(nodeId, 'xc') * AMBIENT_CYCLE_RANGE_SEC,
		yCycleSec: AMBIENT_MIN_CYCLE_SEC + hashFraction(nodeId, 'yc') * AMBIENT_CYCLE_RANGE_SEC,
	};
}

export function getAmbientElapsedDeltaSec(previousMs: number, nowMs: number): number {
	return Math.max(0, Math.min(nowMs - previousMs, AMBIENT_MAX_FRAME_DELTA_MS)) / 1000;
}

export function getAmbientOffset(
	profile: AmbientMotionProfile,
	elapsedSec: number,
): { x: number; y: number } {
	return {
		x: Math.sin((elapsedSec / profile.xCycleSec) * TAU + profile.xPhase) * profile.xAmplitude,
		y: Math.sin((elapsedSec / profile.yCycleSec) * TAU + profile.yPhase) * profile.yAmplitude,
	};
}

export function applyAmbientOffsets(
	positions: ReadonlyMap<string, NodePositionPct>,
	profiles: ReadonlyMap<string, AmbientMotionProfile>,
	elapsedSec: number,
): ReadonlyMap<string, NodePositionPct> {
	const ambientPositions = new Map<string, NodePositionPct>();
	for (const [nodeId, position] of positions) {
		const profile = profiles.get(nodeId);
		if (!profile) {
			ambientPositions.set(nodeId, position);
			continue;
		}
		const offset = getAmbientOffset(profile, elapsedSec);
		ambientPositions.set(nodeId, {
			...position,
			x: position.x + offset.x,
			y: position.y + offset.y,
		});
	}
	return ambientPositions;
}

export function useSpiderwebAmbientPositions(
	nodes: WebNodeConfig[],
	basePositions: ReadonlyMap<string, NodePositionPct>,
	active: boolean,
	animationsPaused: boolean,
): ReadonlyMap<string, NodePositionPct> {
	const profiles = useMemo(
		() => new Map(nodes.map((node) => [node.id, getAmbientMotionProfile(node.id)] as const)),
		[nodes],
	);
	const frameIntervalMs = useMemo(
		() =>
			nodes.some((node) => node.overlapColors?.length)
				? AMBIENT_OVERLAP_FRAME_INTERVAL_MS
				: AMBIENT_FRAME_INTERVAL_MS,
		[nodes],
	);
	const [elapsedSec, setElapsedSec] = useState(0);
	const elapsedSecRef = useRef(0);
	const previousFrameMsRef = useRef<number | null>(null);
	const lastRenderMsRef = useRef(0);

	useEffect(() => {
		if (!active || animationsPaused) {
			previousFrameMsRef.current = null;
			return;
		}

		let frameId = 0;
		const tick = (nowMs: number) => {
			const previousFrameMs = previousFrameMsRef.current;
			previousFrameMsRef.current = nowMs;
			if (previousFrameMs !== null) {
				elapsedSecRef.current += getAmbientElapsedDeltaSec(previousFrameMs, nowMs);
			}
			if (nowMs - lastRenderMsRef.current >= frameIntervalMs) {
				lastRenderMsRef.current = nowMs;
				setElapsedSec(elapsedSecRef.current);
			}
			frameId = requestAnimationFrame(tick);
		};

		frameId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frameId);
	}, [active, animationsPaused, frameIntervalMs]);

	return useMemo(
		() => (active ? applyAmbientOffsets(basePositions, profiles, elapsedSec) : basePositions),
		[active, basePositions, elapsedSec, profiles],
	);
}
